import { useState } from 'react';
import { Box, Flex, Text, HStack, VStack, Button, useBreakpointValue } from '@skin-seoul/design-system';
import type { Review } from '..';
import { StarIcon } from '../assets/icons/StarIcon';
import { VerifiedIcon } from '../assets/icons/VerifiedIcon';
import { ChevronIcon } from '../assets/icons/ChevronIcon';

import {
  parseISO,
  differenceInSeconds,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  format,
} from 'date-fns';

export const formatRelativeTime = (dateString: string): string => {
  const date = parseISO(dateString);
  const now = new Date();

  const diffInSeconds = differenceInSeconds(now, date);
  const diffInMinutes = differenceInMinutes(now, date);
  const diffInHours = differenceInHours(now, date);
  const diffInDays = differenceInDays(now, date);

  if (diffInSeconds < 60) {
    return `${diffInSeconds} seconds ago`;
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes} ${diffInMinutes === 1 ? 'min' : 'mins'} ago`;
  } else if (diffInHours < 24) {
    return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
  } else if (diffInDays < 30) {
    return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
  } else {
    return format(date, 'yyyy.MM.dd');
  }
};

interface ReviewCardProps {
  review: Review;
  onImageClick?: () => void;
  isLastInColumn?: boolean;
}
//TODO: need to check which image click for this event
export const ReviewCard = ({ review, onImageClick, isLastInColumn = false }: ReviewCardProps) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const hasImages = Boolean(review.images && review.images.length > 0);
  const isMobile = useBreakpointValue({ base: true, tablet: false });

  const handlePrevImage = () => {
    if (hasImages && review.images && review.images.length > 0) {
      const imagesLength = review.images.length;
      setActiveImageIndex((prev) => (prev === 0 ? imagesLength - 1 : prev - 1));
    }
  };

  const handleNextImage = () => {
    if (hasImages && review.images && review.images.length > 0) {
      const imagesLength = review.images.length;
      setActiveImageIndex((prev) => (prev === imagesLength - 1 ? 0 : prev + 1));
    }
  };

  const handleThumbnailClick = (index: number) => {
    setActiveImageIndex(index);
  };

  return (
    <Box mb="16px">
      <Flex justify="space-between" align="center" mb="12px" mt={{ base: '12px', tablet: '0' }}>
        <HStack gap="8px" color="#8a8a8a">
          <VerifiedIcon />
          <Text fontFamily="Plus Jakarta Sans" style={{ fontSize: '12px' }}>
            Verified purchase
          </Text>
        </HStack>
        <Text color="#8a8a8a" fontFamily="Plus Jakarta Sans" style={{ fontSize: '12px' }} fontWeight="400">
          {formatRelativeTime(review.createdAt)}
        </Text>
      </Flex>

      {isMobile ? (
        <>
          <VStack p="12px" bg="#f7f7f7" borderRadius="16px" gap="24px" mb="12px">
            {hasImages && review.images && (
              <Box position="relative" w="full" h="358px">
                <Box
                  w="full"
                  h="full"
                  borderRadius="12px"
                  bgImage={`url(${review.images[activeImageIndex].url})`}
                  bgSize="cover"
                  backgroundPosition="center"
                  position="relative"
                >
                  {review.images.length > 1 && (
                    <>
                      <Button
                        position="absolute"
                        top="50%"
                        left="8px"
                        transform="translateY(-50%)"
                        w="32px"
                        h="32px"
                        borderRadius="50%"
                        bg="rgba(247, 247, 249, 0.2)"
                        backdropFilter="blur(16px)"
                        border="none"
                        _hover={{ bg: 'rgba(247, 247, 249, 0.4)' }}
                        onClick={handlePrevImage}
                      >
                        <ChevronIcon direction="left" />
                      </Button>
                      <Button
                        position="absolute"
                        top="50%"
                        right="8px"
                        transform="translateY(-50%)"
                        w="32px"
                        h="32px"
                        borderRadius="50%"
                        bg="rgba(247, 247, 249, 0.2)"
                        backdropFilter="blur(16px)"
                        border="none"
                        _hover={{ bg: 'rgba(247, 247, 249, 0.4)' }}
                        onClick={handleNextImage}
                      >
                        <ChevronIcon direction="right" />
                      </Button>
                      <HStack
                        position="absolute"
                        bottom="8px"
                        left="50%"
                        transform="translateX(-50%)"
                        gap="2px"
                        p="8px"
                        borderRadius="23px"
                        bg="rgba(247, 247, 249, 0.2)"
                        backdropFilter="blur(16px)"
                      >
                        {review.images.map((_, index) => (
                          <Box
                            key={index}
                            w={index === activeImageIndex ? '20px' : '4px'}
                            h="4px"
                            borderRadius="4px"
                            bg={index === activeImageIndex ? '#ffffff' : '#c4c4c4'}
                            transition="all 0.2s ease"
                          />
                        ))}
                      </HStack>
                    </>
                  )}
                </Box>
              </Box>
            )}

            <VStack gap="16px" align="start" w="full">
              <Text
                fontFamily="Plus Jakarta Sans"
                style={{ fontSize: '18px', lineHeight: '1.4' }}
                fontWeight="500"
                color="#2a2a2a"
                m={0}
              >
                {review.title}
              </Text>
              <Text
                fontFamily="Plus Jakarta Sans"
                style={{ fontSize: '14px', lineHeight: '1.4' }}
                fontWeight="400"
                color="#474747"
                m={0}
              >
                {review.content}
              </Text>
            </VStack>
          </VStack>

          <Flex justify="space-between" align="center" mt="12px">
            <HStack gap="8px">
              <Box
                w="20px"
                h="20px"
                borderRadius="50%"
                bg="#fff3ee"
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{ fontSize: '12px' }}
                fontWeight="bold"
                color="#8a8a8a"
                overflow="hidden"
              >
                {review.author.name.charAt(0).toUpperCase()}
              </Box>
              <Text fontFamily="Plus Jakarta Sans" style={{ fontSize: '12px' }} color="#8a8a8a" fontWeight="400">
                {review.author.name}
              </Text>
              <HStack gap="0">
                {[...Array(review.rating)].map((_, index) => (
                  <StarIcon width={16} height={16} key={index} />
                ))}
              </HStack>
            </HStack>
          </Flex>

          <Box w="full" h="1px" bg="#f7f7f9" my="12px" />

          <VStack gap="4px" align="start">
            <HStack gap="4px">
              <Text color="#8a8a8a" fontFamily="Plus Jakarta Sans" style={{ fontSize: '12px' }} fontWeight="400">
                Skin type:
              </Text>
              <Text color="#8a8a8a" fontFamily="Plus Jakarta Sans" style={{ fontSize: '12px' }} fontWeight="400">
                {review.skinType.join(', ')}
              </Text>
            </HStack>
            {review.skinConcern && (
              <HStack gap="4px">
                <Text color="#8a8a8a" fontFamily="Plus Jakarta Sans" style={{ fontSize: '12px' }} fontWeight="400">
                  Skin concern:
                </Text>
                <Text color="#8a8a8a" fontFamily="Plus Jakarta Sans" style={{ fontSize: '12px' }} fontWeight="400">
                  {review.skinConcern.join(', ')}
                </Text>
              </HStack>
            )}
          </VStack>
        </>
      ) : (
        <>
          <Flex
            p="16px"
            bg="#f7f7f7"
            borderRadius="16px"
            alignItems="flex-start"
            gap="16px"
            mb="12px"
            direction={hasImages ? 'row' : 'column'}
          >
            {hasImages && review.images && (
              <Box>
                <Box position="relative" flex="0 0 208px" w="208px" h="240px">
                  <Box
                    w="full"
                    h="full"
                    borderRadius="8px"
                    bgImage={`url(${review.images[activeImageIndex].url})`}
                    bgSize="cover"
                    backgroundPosition="center"
                    position="relative"
                  >
                    {review.images.length > 1 && (
                      <>
                        <Button
                          position="absolute"
                          top="50%"
                          left="4px"
                          transform="translateY(-50%)"
                          w="24px"
                          h="24px"
                          borderRadius="50%"
                          bg="rgba(247, 247, 249, 0.2)"
                          backdropFilter="blur(10.76px)"
                          border="none"
                          _hover={{ bg: 'rgba(247, 247, 249, 0.4)' }}
                          onClick={handlePrevImage}
                        >
                          <ChevronIcon direction="left" />
                        </Button>
                        <Button
                          position="absolute"
                          top="50%"
                          right="4px"
                          transform="translateY(-50%)"
                          w="24px"
                          h="24px"
                          borderRadius="50%"
                          bg="rgba(247, 247, 249, 0.2)"
                          backdropFilter="blur(10.76px)"
                          border="none"
                          _hover={{ bg: 'rgba(247, 247, 249, 0.4)' }}
                          onClick={handleNextImage}
                        >
                          <ChevronIcon direction="right" />
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
                {review.images.length > 1 && (
                  <HStack gap="8px" alignItems="flex-end" w="208px" mt="16px">
                    {review.images.slice(0, 3).map((image, index) => (
                      <Box
                        key={image.id}
                        w="64px"
                        h="64px"
                        borderRadius="8px"
                        bgImage={`url(${image.url})`}
                        bgSize="cover"
                        backgroundPosition="center"
                        cursor="pointer"
                        transition="transform 0.2s ease"
                        border={index === activeImageIndex ? '1.6px solid #6f3fd7' : 'none'}
                        position="relative"
                        _hover={{ transform: 'scale(1.05)' }}
                        _after={
                          index === activeImageIndex
                            ? {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                bg: 'rgba(111, 63, 215, 0.3)',
                                borderRadius: '8px',
                              }
                            : {}
                        }
                        onClick={() => handleThumbnailClick(index)}
                      />
                    ))}
                  </HStack>
                )}
              </Box>
            )}

            <Box flex="1">
              <Text
                fontFamily="Plus Jakarta Sans"
                style={{ fontSize: '18px', lineHeight: '1.4' }}
                fontWeight="500"
                color="#2a2a2a"
                m="0 0 8px 0"
              >
                {review.title}
              </Text>
              <Text
                fontFamily="Plus Jakarta Sans"
                style={{ fontSize: '14px', lineHeight: '1.4' }}
                fontWeight="400"
                color="#474747"
                m={0}
              >
                {review.content}
              </Text>
            </Box>
          </Flex>

          <Flex justify="space-between" align="center" mt="16px">
            <HStack gap="8px">
              <Box
                w="20px"
                h="20px"
                borderRadius="50%"
                bg="#f0f0f0"
                display="flex"
                alignItems="center"
                justifyContent="center"
                style={{ fontSize: '12px' }}
                fontWeight="bold"
                color="#8a8a8a"
              >
                {review.author.name.charAt(0).toUpperCase()}
              </Box>
              <Text style={{ fontSize: '12px' }} color="#8a8a8a" fontWeight="400">
                {review.author.name}
              </Text>
              <HStack gap="4px">
                {[...Array(review.rating)].map((_, index) => (
                  <StarIcon width={16} height={16} key={index} />
                ))}
              </HStack>
            </HStack>
          </Flex>

          <Flex
            justify="space-between"
            my="16px"
            direction={{ base: 'column', tablet: 'row' }}
            gap={{ base: '4px', tablet: '0' }}
          >
            <HStack gap="8px">
              <Text color="#8a8a8a" fontFamily="Plus Jakarta Sans" style={{ fontSize: '12px' }} fontWeight="400">
                Skin type:
              </Text>
              <Text color="#8a8a8a" fontFamily="Plus Jakarta Sans" style={{ fontSize: '12px' }} fontWeight="400">
                {review.skinType.join(', ')}
              </Text>
            </HStack>
            {review.skinConcern && (
              <HStack gap="8px">
                <Text color="#8a8a8a" fontFamily="Plus Jakarta Sans" style={{ fontSize: '12px' }} fontWeight="400">
                  Skin concern:
                </Text>
                <Text color="#8a8a8a" fontFamily="Plus Jakarta Sans" style={{ fontSize: '12px' }} fontWeight="400">
                  {review.skinConcern.join(', ')}
                </Text>
              </HStack>
            )}
          </Flex>
        </>
      )}

      {!isLastInColumn && <Box w="full" my="12px" bg="#dcdcdc" h="1px" />}
    </Box>
  );
};

export default ReviewCard;
